/** @eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core'

import { buffer, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { ulid } from 'ulid'

import {
  AppFeatures,
  CustomSensorPrefix,
  DeviceSensorParameterSetting,
  DeviceSensorParameterSettings,
  DeviceSensorsConfig,
  EngineHoursSourceTypes,
  OdometerSourceTypes,
  POSITION_VALID_CHECK,
  TelemetryReadMode,
  Vehicle,
} from '@ti/contracts'
import {
  CurrentStatusResponse,
  LegacyTelemetryBinding,
} from '@ti/core/api/scopes/fleet/routes/vehicles.route'
import { ONE_MINUTE_MS, ONE_SECOND_MS } from '@ti/core/common/constants'
import { RangeMs } from '@ti/core/common/contracts'
import { PricingPlanService } from '@ti/core/common/services'
import { getDtcCodeString, stripEmptyKeys } from '@ti/core/common/utils'
import { TelemetryPositionData, VehicleTelemetry } from '@ti/core/contracts/vehicle'
import { SubscriptionItem } from '@ti/core/flespi-listener'
import {
  BaseFlespiClient,
  FlespiSubscriptionRef,
  FlespiTelemetryData,
  FlespiTopicMessage,
} from '@ti/core/flespi-providers'
import { VehicleStatus } from '@ti/shared/map/contracts'

/** Number of flespi messages loaded per pagination chunk during historical loading. */
const HISTORICAL_PAGE_SIZE = 5000
const LEGACY_HISTORICAL_PAGE_SIZE = 5001
const LEGACY_HISTORICAL_MAX_PAGES = 128
const LEGACY_HISTORICAL_MAX_BYTES = 5_000_000
const LEGACY_HISTORICAL_MAX_ROWS = 100_000
const LEGACY_HISTORICAL_MAX_VEHICLES = 128
const LEGACY_TELEMETRY_BINDING_MAX_BYTES = 64_000

/** How many minutes before the range start to pre-load to ensure rarely-emitted params are available. */
const HISTORICAL_PRELOAD_MINUTES = 10

/** One point in a device's historical telemetry timeline. */
export interface HistoricalTelemetryEntry {
  deviceId: number
  timestamp: number
  telemetry: VehicleTelemetry
}

export class HistoricalTelemetryUnavailableError extends Error {
  public constructor(reason: string) {
    super(`Historical telemetry is unavailable: ${reason}`)
    this.name = 'HistoricalTelemetryUnavailableError'
  }
}

/** A calculator value applied to entries at or after `fromMs`, as measured at `measuredAtMs`. */
interface HistoricalCalculatorValue {
  value: number
  fromMs: number
  measuredAtMs: number
}

interface HistoricalCalculatorValues {
  odometer: HistoricalCalculatorValue[]
  engineHours: HistoricalCalculatorValue[]
}

@Injectable({ providedIn: 'root' })
export class FlespiClient extends BaseFlespiClient {
  protected readonly pricingPlanService = inject(PricingPlanService)

  protected readonly flespiSensorsParameters = Object.values(DeviceSensorParameterSettings).map(
    (value: DeviceSensorParameterSetting) => value.flespiPluginParameter,
  )

  protected readonly TELEMETRY_FIELDS = [
    'device.id',
    'position',
    'wialon.address',
    'timestamp',
    'server.timestamp',
    'sleep.mode.status',
    'wialon.speed.limit',
    'wialon.overspeeding.event',
    ...this.flespiSensorsParameters,
  ]

  public async loadDataEngineSecondaryData(vehicle: Vehicle) {
    const deviceId = vehicle.primaryExternalDeviceId
    const deviceConfig = vehicle.primaryDevice?.sensorsConfig ?? {}
    const accessToUnifiedSensors = await this.pricingPlanService.areFeaturesAllowed([
      AppFeatures.DeviceUnifiedSensors,
    ])

    const [engineHoursRes, mileageRes] = await Promise.all([
      // Load calculator engine hours value if configured to use calculator
      accessToUnifiedSensors &&
      deviceConfig?.engine_hours_source_type &&
      deviceConfig.engine_hours_source_type !== EngineHoursSourceTypes.calculator
        ? Promise.resolve(null)
        : this.loadTotalEngineHours(deviceId),

      // Load calculator mileage value if not configured to use telemetry
      deviceConfig?.odometer_source_type &&
      deviceConfig.odometer_source_type !== OdometerSourceTypes.calculator
        ? Promise.resolve(null)
        : this.loadTotalMileage(deviceId),
    ])

    const result: VehicleTelemetry[] = []
    if (engineHoursRes) {
      result.push({
        deviceId,
        engineHours: Math.round(engineHoursRes.total_duration / 3600),
        timestamps: { engineHours: Math.round(engineHoursRes.timestamp * 1000) },
      } as VehicleTelemetry)
    }

    if (mileageRes) {
      result.push({
        deviceId,
        odometer: mileageRes.total_mileage,
        timestamps: { odometer: Math.round(mileageRes.timestamp * 1000) },
      } as VehicleTelemetry)
    }

    return result
  }

  public async loadInitialTelemetry(vehicles: Vehicle[]) {
    const flespiToken = await this.getToken()
    if (!flespiToken) {
      console.warn(`Flespi token is missing!`)
      return []
    }

    const telemetryFields = this.getTelemetryFieldsToLoad(vehicles)
    const deviceConfigMap = this.getDeviceConfigMap(vehicles)
    const vehicleIdByExternalId: Record<number, string> = {}
    vehicles.forEach((vehicle) => {
      vehicleIdByExternalId[vehicle.primaryExternalDeviceId] = vehicle.id
    })
    const result = await this.loadInitialTelemetryByFields(telemetryFields)
    if (result?.length > 0) {
      // if telemetry last position valid false - we need to get last valid position if it exists
      const vehicleIdsWithFalsePosition: string[] = []
      result.forEach((item) => {
        if (
          item.id &&
          item.telemetry?.position?.value?.valid === false &&
          vehicleIdByExternalId[item.id]
        ) {
          vehicleIdsWithFalsePosition.push(vehicleIdByExternalId[item.id])
        }
      })
      const messageWithValidPositionByDeviceId: Record<number, Record<string, any>> = {}
      const positionFields = ['altitude', 'latitude', 'longitude', 'direction', 'valid', 'speed']
      const fields = ['device.id', 'timestamp', 'wialon.address']
      if (vehicleIdsWithFalsePosition?.length) {
        positionFields.forEach((field) => fields.push(`position.${field}`))
        try {
          const messages = await this.api.fleet.vehicle.massMessages(vehicleIdsWithFalsePosition, {
            vehicleIds: vehicleIdsWithFalsePosition,
            fields,
            filter: POSITION_VALID_CHECK,
            count: 1,
            reverse: true,
          })
          messages.forEach((message) => {
            if (message['device.id']) {
              messageWithValidPositionByDeviceId[message['device.id']] = message
            }
          })
        } catch (e) {
          console.error('Error to get additional info about last valid position', e)
        }
      }
      return result
        .filter((data) => data.id && data.telemetry)
        .map((data) => {
          // change for valid position from message if in telemetry not valid
          if (
            data.telemetry?.position?.value?.valid === false &&
            data.id &&
            messageWithValidPositionByDeviceId[data.id]
          ) {
            const message = messageWithValidPositionByDeviceId[data.id]
            const positionTimestamp = message.timestamp
            delete message.timestamp
            delete message['device.id']
            const address = message['wialon.address']
            delete message['wialon.address']
            data.telemetry.position.value = {}
            positionFields.forEach((field) => {
              const param = `position.${field}`
              if (message[param] !== undefined) {
                data.telemetry.position.value[field] = message[param]
              }
            })
            data.telemetry.position.ts = positionTimestamp
            if (address) {
              data.telemetry['wialon.address'] = {
                value: address,
                ts: positionTimestamp,
              }
            }
          }
          return this.flespiTelemetryToVehicleTelemetry(data.telemetry, deviceConfigMap)
        })
    }
    return []
  }

  public async loadDataEngineStatuses(vehicles: Vehicle[]) {
    const deviceIds = vehicles.map((v) => v.primaryExternalDeviceId).filter(Boolean)
    if (!deviceIds.length) return []

    const response = await this.loadMassCurrentStatuses(deviceIds)
    return response && typeof response === 'object' && !Array.isArray(response)
      ? Object.values(response)
          .filter((data: CurrentStatusResponse) => data.device_id)
          .map((data: CurrentStatusResponse) => {
            return {
              deviceId: data.device_id,
              lastStatus:
                data.name === 'moving_status'
                  ? data.moving
                    ? VehicleStatus.Running
                    : VehicleStatus.Stopped
                  : VehicleStatus.Idling,
              lastStatusChangeAt: Math.round(data.begin * 1000),
              timestamps: {
                lastStatus: Math.round(data.end * 1000),
              },
            } as VehicleTelemetry
          })
      : []
  }

  protected async loadHistoricalMovingStatuses(vehicle: Vehicle, range: RangeMs) {
    const timestampFrom = Math.floor(range[0] / 1000)
    const timestampTo = Math.floor(range[1] / 1000)
    const fields = 'begin,end,device_ti_id,device_id,active,moving'
    const backendMovingStatus = await this.api.fleet.vehicle.movingStatusIntervals(vehicle.id, {
      deviceId: vehicle.primaryExternalDeviceId,
      timestampFrom,
      timestampTo,
      fields,
    })
    return backendMovingStatus ?? []
  }

  protected async loadHistoricalIdlingStatuses(vehicle: Vehicle, range: RangeMs): Promise<any[]> {
    const flespiToken = await this.getToken()
    const timestampFrom = Math.floor(range[0] / 1000)
    const timestampTo = Math.floor(range[1] / 1000)
    const fields = 'begin,end,device_ti_id,device_id,active,moving'
    const options = {
      headers: { Authorization: `FlespiToken ${flespiToken}` },
      params: {
        data: JSON.stringify({
          begin: timestampFrom,
          end: timestampTo,
          fields,
        }),
      },
    }
    const idlingResponse = await firstValueFrom(
      this.http.get<{ result: Array<any> }>(
        `${this.config.flespiUrl}/gw/calcs/name="idling_status"/devices/${vehicle.primaryExternalDeviceId}/intervals/all`,
        options,
      ),
    )
    return idlingResponse.result
  }

  public async loadHistoricalStatuses(vehicle: Vehicle, range: RangeMs) {
    const idlingStatusIntervals = await this.loadHistoricalIdlingStatuses(vehicle, range)
    const movingStatusIntervals = await this.loadHistoricalMovingStatuses(vehicle, range)

    const mergedIntervals = mergeIntervals([
      movingStatusIntervals.map((data) => ({ ...data, name: 'moving_status', active: true })),
      idlingStatusIntervals.map((data) => ({ ...data, name: 'idling_status', active: true })),
    ])

    const deviceConfigMap = this.getDeviceConfigMap([vehicle])
    const statusIntervalsStateMap = new Map()
    return mergedIntervals
      .map((data) =>
        this.flespiCalcsToVehicleTelemetry(
          { data: data, topicData: { device_id: vehicle.primaryExternalDeviceId } } as any,
          deviceConfigMap,
          statusIntervalsStateMap,
        ),
      )
      .filter(Boolean) as VehicleTelemetry[]
  }

  /**
   * Loads historical telemetry messages for the given vehicles over the specified range,
   * with a 10-minute pre-load before rangeStart so rarely-emitted parameters have a chance
   * to have been reported before the range start.
   *
   * Every raw message is converted into a `VehicleTelemetry` snapshot via the same
   * `flespiTelemetryToVehicleTelemetry` logic used for real-time data, carrying forward all
   * previously-seen parameter values (mirrors flespi's own "last known value" semantics).
   * The result is an append-only per-device timeline, so callers can look up the state at
   * any point in time without re-fetching.
   *
   * @param vehicles           Vehicles to load telemetry for.
   * @param totalRange         The full historical range [startMs, endMs] to load.
   * @param customSensorFields Custom sensor flespi field names (`custom_sensor_<id>`) to
   *                           include in the selection - real-time telemetry never loads
   *                           these via HTTP, so the caller must supply them explicitly.
   * @param onChunkCb          Called after each page with the newly-built timeline entries.
   * @param signal             Optional AbortSignal to cancel loading mid-flight.
   */
  public async loadHistoricalTelemetry(
    vehicles: Vehicle[],
    totalRange: RangeMs,
    customSensorFields: string[],
    onChunkCb: (entries: HistoricalTelemetryEntry[]) => void,
    signal?: AbortSignal,
    options?: { telemetryReadMode?: TelemetryReadMode; isCurrent?: () => boolean },
  ): Promise<void> {
    if (!vehicles.length) return

    if (options?.telemetryReadMode === 'new') {
      throw new HistoricalTelemetryUnavailableError('the requested map source is not available')
    }
    if (options?.telemetryReadMode === 'legacy') {
      await this.loadLegacyHistoricalTelemetry(
        vehicles,
        totalRange,
        customSensorFields,
        onChunkCb,
        signal,
        options.isCurrent,
      )
      return
    }

    const vehicleIds = vehicles.map((v) => v.id)
    const deviceConfigMap = this.getDeviceConfigMap(vehicles)

    // Odometer/engine-hours calculator values (for devices configured to use them) - fetched
    // once upfront and applied per-entry by timestamp, see findCalcValueAtOrBefore.
    const calculatorValuesByDevice = await this.loadHistoricalCalculatorValues(
      vehicles,
      deviceConfigMap,
      totalRange,
    )

    // Pre-load starts 10 minutes before the range start
    const fetchFrom = totalRange[0] - HISTORICAL_PRELOAD_MINUTES * ONE_MINUTE_MS
    const fetchTo = totalRange[1]

    // Fields we need from each message (flat flespi message format)
    const fields = Array.from(
      new Set([
        'device.id',
        'timestamp',
        'server.timestamp',
        'position.latitude',
        'position.longitude',
        'position.altitude',
        'position.direction',
        'position.speed',
        'position.valid',
        'wialon.address',
        'sleep.mode.status',
        'wialon.speed.limit',
        'wialon.overspeeding.event',
        ...this.flespiSensorsParameters,
        ...customSensorFields,
      ]),
    )

    // Per-device accumulated state, in the same nested shape `flespiTelemetryToVehicleTelemetry`
    // expects, so every message can be converted via the shared real-time conversion logic.
    const deviceFieldState = new Map<number, FlespiTelemetryData>()

    let pageFrom = fetchFrom
    let hasMore = true

    while (hasMore && !signal?.aborted) {
      const messages = await this.api.fleet.vehicle.massMessages(vehicleIds, {
        vehicleIds,
        fields,
        timestampFrom: pageFrom / 1000,
        timestampTo: fetchTo / 1000,
        count: HISTORICAL_PAGE_SIZE,
        reverse: false,
      })

      if (!messages.length) break

      // Advance the pagination cursor past the last message timestamp
      const lastTs = messages[messages.length - 1].timestamp as number
      pageFrom = lastTs * 1000 + 1 // convert flespi seconds to ms then +1ms
      hasMore = messages.length === HISTORICAL_PAGE_SIZE

      const entries: HistoricalTelemetryEntry[] = []

      for (const message of messages) {
        const deviceId = message['device.id'] as number
        if (!deviceId) continue

        let fieldState = deviceFieldState.get(deviceId)
        if (!fieldState) {
          fieldState = {}
          deviceFieldState.set(deviceId, fieldState)
        }

        this.mergeFlatMessageIntoFieldState(message, fieldState)

        const timestampMs = Math.round((message.timestamp as number) * 1000)
        const telemetry = this.flespiTelemetryToVehicleTelemetry(
          fieldState,
          deviceConfigMap,
          timestampMs,
        )
        telemetry.lastStatus = deriveHistoricalVehicleStatus(telemetry)

        const calculatorValues = calculatorValuesByDevice.get(deviceId)
        if (calculatorValues?.odometer.length) {
          const odometer = findCalcValueAtOrBefore(calculatorValues.odometer, timestampMs)
          telemetry.odometer = odometer.value
          telemetry.timestamps = { ...telemetry.timestamps, odometer: odometer.measuredAtMs }
        }
        if (calculatorValues?.engineHours.length) {
          const engineHours = findCalcValueAtOrBefore(calculatorValues.engineHours, timestampMs)
          telemetry.engineHours = engineHours.value
          telemetry.timestamps = { ...telemetry.timestamps, engineHours: engineHours.measuredAtMs }
        }

        telemetry.isHistorical = true

        entries.push({ deviceId, timestamp: timestampMs, telemetry })
      }

      if (entries.length) {
        onChunkCb(entries)
      }
    }
  }

  private async loadLegacyHistoricalTelemetry(
    vehicles: Vehicle[],
    totalRange: RangeMs,
    customSensorFields: string[],
    onChunkCb: (entries: HistoricalTelemetryEntry[]) => void,
    signal?: AbortSignal,
    isCurrent?: () => boolean,
  ): Promise<void> {
    const fetchFrom = totalRange[0] - HISTORICAL_PRELOAD_MINUTES * ONE_MINUTE_MS
    const fetchTo = totalRange[1]
    const fromSeconds = fetchFrom / 1000
    const toSeconds = fetchTo / 1000
    if (
      !Number.isFinite(fromSeconds) ||
      !Number.isFinite(toSeconds) ||
      toSeconds < fromSeconds ||
      vehicles.length > LEGACY_HISTORICAL_MAX_VEHICLES
    ) {
      throw new HistoricalTelemetryUnavailableError(
        'the selected range or vehicle count is invalid',
      )
    }

    const targets = new Set<string>()
    const deviceIds = new Set<number>()
    const bindings: LegacyTelemetryBinding[] = []
    const frozenVehicles = vehicles.map((vehicle) => {
      const deviceId = vehicle.primaryExternalDeviceId
      const config = vehicle.primaryDevice?.sensorsConfig
      if (
        !vehicle.id ||
        targets.has(vehicle.id) ||
        !Number.isSafeInteger(deviceId) ||
        deviceId <= 0 ||
        deviceIds.has(deviceId) ||
        config?.odometer_source_type !== OdometerSourceTypes.telemetry ||
        config?.engine_hours_source_type !== EngineHoursSourceTypes.telemetry
      ) {
        throw new HistoricalTelemetryUnavailableError(
          'a selected vehicle has unsupported telemetry settings',
        )
      }
      let serializedConfig: string
      try {
        serializedConfig = JSON.stringify(config)
      } catch {
        throw new HistoricalTelemetryUnavailableError('a selected vehicle binding cannot be frozen')
      }
      if (typeof serializedConfig !== 'string') {
        throw new HistoricalTelemetryUnavailableError('a selected vehicle binding is too large')
      }
      const frozenConfig = JSON.parse(serializedConfig) as DeviceSensorsConfig
      const binding: LegacyTelemetryBinding = {
        vehicleId: vehicle.id,
        externalDeviceId: deviceId,
        sensorsConfig: frozenConfig,
      }
      if (
        new TextEncoder().encode(JSON.stringify(binding)).byteLength >
        LEGACY_TELEMETRY_BINDING_MAX_BYTES
      ) {
        throw new HistoricalTelemetryUnavailableError('a selected vehicle binding is too large')
      }
      targets.add(vehicle.id)
      deviceIds.add(deviceId)
      bindings.push(binding)
      return {
        ...vehicle,
        primaryExternalDeviceId: deviceId,
        primaryDevice: vehicle.primaryDevice
          ? { ...vehicle.primaryDevice, sensorsConfig: frozenConfig }
          : vehicle.primaryDevice,
      }
    })

    const vehicleBindingById = new Map(bindings.map((binding) => [binding.vehicleId, binding]))
    const deviceConfigMap = this.getDeviceConfigMap(frozenVehicles)
    const fields = Array.from(
      new Set([
        'device.id',
        'timestamp',
        'server.timestamp',
        'position.latitude',
        'position.longitude',
        'position.altitude',
        'position.direction',
        'position.speed',
        'position.valid',
        'wialon.address',
        'sleep.mode.status',
        'wialon.speed.limit',
        'wialon.overspeeding.event',
        ...this.flespiSensorsParameters,
        ...customSensorFields,
      ]),
    )

    const deviceFieldState = new Map<number, FlespiTelemetryData>()
    let pages = 0
    let observedRows = 0
    let observedBytes = 0

    for (const vehicle of frozenVehicles) {
      const deviceId = vehicle.primaryExternalDeviceId
      let pageFrom = fromSeconds
      let lastTimestamp: number | undefined
      let lastBoundary: string | undefined
      let hasMore = true

      while (hasMore) {
        if (!this.isLegacyRunCurrent(signal, isCurrent)) return
        if (pages >= LEGACY_HISTORICAL_MAX_PAGES) {
          throw new HistoricalTelemetryUnavailableError('the page limit was reached')
        }
        pages++

        let messages: unknown
        try {
          messages = await this.api.fleet.vehicle.massMessages(
            [vehicle.id],
            {
              vehicleIds: [vehicle.id],
              fields,
              timestampFrom: pageFrom,
              timestampTo: toSeconds,
              count: LEGACY_HISTORICAL_PAGE_SIZE,
              reverse: false,
            },
            'legacy',
            vehicleBindingById.get(vehicle.id),
          )
        } catch {
          throw new HistoricalTelemetryUnavailableError('the message source rejected the request')
        }

        if (!this.isLegacyRunCurrent(signal, isCurrent)) return
        if (!Array.isArray(messages) || messages.length > LEGACY_HISTORICAL_PAGE_SIZE) {
          throw new HistoricalTelemetryUnavailableError('the message response shape is invalid')
        }

        const serialized = JSON.stringify(messages)
        if (typeof serialized !== 'string') {
          throw new HistoricalTelemetryUnavailableError('the message response could not be bounded')
        }
        observedBytes += new TextEncoder().encode(serialized).byteLength
        observedRows += messages.length
        if (
          observedBytes > LEGACY_HISTORICAL_MAX_BYTES ||
          observedRows > LEGACY_HISTORICAL_MAX_ROWS
        ) {
          throw new HistoricalTelemetryUnavailableError('the response size limit was reached')
        }

        if (messages.length === 0) {
          if (lastBoundary !== undefined) {
            throw new HistoricalTelemetryUnavailableError(
              'a continuation page lost its boundary row',
            )
          }
          hasMore = false
          continue
        }

        const rawMessages = messages as Array<Record<string, unknown>>
        let firstMessageIndex = 0
        if (lastBoundary !== undefined) {
          const boundary = rawMessages[0]
          if (
            !boundary ||
            boundary['device.id'] !== deviceId ||
            boundary.timestamp !== lastTimestamp ||
            stableJson(boundary) !== lastBoundary
          ) {
            throw new HistoricalTelemetryUnavailableError(
              'a continuation page changed its boundary row',
            )
          }
          firstMessageIndex = 1
        }

        const entries: HistoricalTelemetryEntry[] = []
        let previousTimestamp = lastTimestamp
        for (let index = firstMessageIndex; index < rawMessages.length; index++) {
          const message = rawMessages[index]
          const timestamp = message?.timestamp
          if (
            !message ||
            message['device.id'] !== deviceId ||
            typeof timestamp !== 'number' ||
            !Number.isFinite(timestamp) ||
            timestamp < fromSeconds ||
            timestamp > toSeconds ||
            (previousTimestamp !== undefined && timestamp <= previousTimestamp)
          ) {
            throw new HistoricalTelemetryUnavailableError(
              'the message ordering or scope is invalid',
            )
          }
          previousTimestamp = timestamp

          let fieldState = deviceFieldState.get(deviceId)
          if (!fieldState) {
            fieldState = {}
            deviceFieldState.set(deviceId, fieldState)
          }
          this.mergeFlatMessageIntoFieldState(message, fieldState)

          const timestampMs = Math.round(timestamp * 1000)
          const telemetry = this.flespiTelemetryToVehicleTelemetry(
            fieldState,
            deviceConfigMap,
            timestampMs,
          )
          telemetry.lastStatus = deriveHistoricalVehicleStatus(telemetry)
          telemetry.isHistorical = true
          entries.push({ deviceId, timestamp: timestampMs, telemetry })
        }

        const lastMessage = rawMessages[rawMessages.length - 1]
        const nextTimestamp = lastMessage?.timestamp
        if (typeof nextTimestamp !== 'number' || !Number.isFinite(nextTimestamp)) {
          throw new HistoricalTelemetryUnavailableError('the final message timestamp is invalid')
        }
        lastTimestamp = nextTimestamp
        lastBoundary = stableJson(lastMessage)
        pageFrom = nextTimestamp
        hasMore = rawMessages.length === LEGACY_HISTORICAL_PAGE_SIZE

        if (entries.length) {
          if (!this.isLegacyRunCurrent(signal, isCurrent)) return
          onChunkCb(entries)
        }
      }
    }
  }

  private isLegacyRunCurrent(signal?: AbortSignal, isCurrent?: () => boolean): boolean {
    return !signal?.aborted && (isCurrent?.() ?? true)
  }

  /**
   * Loads odometer/engine-hours calculator values for `vehicles` configured to use the
   * calculator source (the default when no source type is set) for the given range. Only
   * fetches the calc(s) a device actually needs, keyed by deviceId for O(1) lookup per entry.
   */
  private async loadHistoricalCalculatorValues(
    vehicles: Vehicle[],
    deviceConfigMap: Map<number, DeviceSensorsConfig>,
    range: RangeMs,
  ): Promise<Map<number, HistoricalCalculatorValues>> {
    const result = new Map<number, HistoricalCalculatorValues>()

    const flespiToken = await this.getToken()
    if (!flespiToken) return result

    await Promise.all(
      vehicles.map(async (vehicle) => {
        const deviceId = vehicle.primaryExternalDeviceId
        if (!deviceId) return

        const deviceConfig = deviceConfigMap.get(deviceId)
        const useOdometerCalculator =
          !deviceConfig?.odometer_source_type ||
          deviceConfig.odometer_source_type === OdometerSourceTypes.calculator
        const useEngineHoursCalculator =
          !deviceConfig?.engine_hours_source_type ||
          deviceConfig.engine_hours_source_type === EngineHoursSourceTypes.calculator

        if (!useOdometerCalculator && !useEngineHoursCalculator) return

        const [odometer, engineHours] = await Promise.all([
          useOdometerCalculator
            ? this.loadCalcValues(deviceId, 'odometer', 'total_mileage', flespiToken, range)
            : Promise.resolve([]),
          useEngineHoursCalculator
            ? this.loadCalcValues(deviceId, 'engine_hours', 'total_duration', flespiToken, range)
            : Promise.resolve([]),
        ])

        if (odometer.length || engineHours.length) {
          result.set(deviceId, { odometer, engineHours })
        }
      }),
    )

    return result
  }

  /**
   * Fetches all odometer/engine-hours calculator intervals for one device within `range` as
   * values sorted ascending by `fromMs` - see `findCalcValueAtOrBefore`, which also propagates
   * the first value backward to entries reported before the calculator produced any.
   */
  private async loadCalcValues(
    deviceId: number,
    calcName: 'odometer' | 'engine_hours',
    valueField: 'total_mileage' | 'total_duration',
    flespiToken: string,
    range: RangeMs,
  ): Promise<HistoricalCalculatorValue[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ result: Array<any> }>(
          `${this.config.flespiUrl}/gw/calcs/name="${calcName}"/devices/${deviceId}/intervals/all`,
          {
            headers: { Authorization: `FlespiToken ${flespiToken}` },
            params: {
              data: JSON.stringify({
                begin: Math.floor(range[0] / 1000),
                end: Math.floor(range[1] / 1000),
                fields:
                  calcName === 'odometer'
                    ? `begin,end,device_id,${valueField},total_mileage_at_start,mileage_steps`
                    : `begin,end,device_id,${valueField}`,
              }),
            },
          },
        ),
      )

      const intervals = (response?.result ?? [])
        .filter((interval) => typeof interval?.[valueField] === 'number')
        .sort((a, b) => a.begin - b.begin)

      if (calcName === 'engine_hours') {
        return intervals.map((interval) => ({
          value: Math.round(interval[valueField] / 3600),
          fromMs: Math.round(interval.begin * 1000),
          measuredAtMs: Math.round(interval.end * 1000),
        }))
      }
      // Steps arrive unordered; the stable sort resolves a shared boundary to the later interval
      return intervals.flatMap(toOdometerCalcValues).sort((a, b) => a.fromMs - b.fromMs)
    } catch (e) {
      console.error(`Error loading historical ${calcName} calculator values`, e)
      return []
    }
  }

  /**
   * Merges one flat `massMessages` row into the accumulated per-device `FlespiTelemetryData`,
   * so the running state always reflects the latest value seen so far for each parameter -
   * matching how flespi reports "last known value" telemetry in real time. `position.*`
   * sub-fields are grouped into a single nested `position` value, same as the real-time shape.
   */
  private mergeFlatMessageIntoFieldState(
    message: Record<string, any>,
    fieldState: FlespiTelemetryData,
  ): void {
    const msgTs = message.timestamp as number // flespi seconds

    fieldState['device.id'] = { value: message['device.id'], ts: msgTs }
    fieldState['timestamp'] = { value: message.timestamp, ts: msgTs }

    Object.keys(message).forEach((key) => {
      if (key === 'device.id' || key === 'timestamp') return

      const value = message[key]
      if (value === undefined || value === null) return

      if (key.startsWith('position.')) {
        const subField = key.slice('position.'.length)
        const existing = fieldState['position']
        fieldState['position'] = {
          ts: msgTs,
          value: { ...(existing?.value ?? {}), [subField]: value },
        }
        return
      }

      fieldState[key] = { value, ts: msgTs }
    })
  }

  public async massLoadVehicleRecentPositions(
    vehicles: Vehicle[],
    count: number = 15,
  ): Promise<Map<string, TelemetryPositionData[]>> {
    const vehicleIds = vehicles.map((v) => v.id)
    if (!vehicleIds.length) return new Map()

    const result = await this.loadMassMessages(vehicleIds, count)
    if (!result.length) {
      return new Map<string, TelemetryPositionData[]>(vehicles.map((vehicle) => [vehicle.id, []]))
    }

    const deviceIdVehicleMap = new Map<number, Vehicle>(
      vehicles.map((vehicle) => [vehicle.primaryExternalDeviceId, vehicle]),
    )
    const vehicleMessageMap = new Map<string, TelemetryPositionData[]>()
    result.forEach((message) => {
      const relatedVehicle = deviceIdVehicleMap.get(message['device.id'])
      if (relatedVehicle) {
        if (!vehicleMessageMap.has(relatedVehicle.id)) {
          vehicleMessageMap.set(relatedVehicle.id, [])
        }

        vehicleMessageMap.get(relatedVehicle.id)!.push({
          latitude: message['position.latitude'],
          longitude: message['position.longitude'],
          timestamp: Math.floor(message.timestamp * 1000),
        })
      }
    })
    return vehicleMessageMap
  }

  public createFlespiSubscription(
    vehicles: Vehicle[],
    token: string,
    onDataCb: (data: VehicleTelemetry[]) => void,
    onAuthFailure?: () => void,
  ): FlespiSubscriptionRef {
    const subscriptionName = `web-app-fleet-client-${ulid().slice(10, 26)}`
    const telemetryFields = this.getTelemetryFieldsToLoad(vehicles)
    const deviceConfigMap = this.getDeviceConfigMap(vehicles)
    // Cache for recent interval statuses for correct applying Idling and Stopped statuses
    const statusIntervalsStateMap = new Map<number, Map<StatusIntervalType, StatusIntervalState>>()

    return this.openSubscription(
      subscriptionName,
      token,
      (destroy$) => [
        this.subscribeToConnectedState(subscriptionName, destroy$, onDataCb),
        this.subscribeToCalcMessages(
          subscriptionName,
          destroy$,
          deviceConfigMap,
          statusIntervalsStateMap,
          onDataCb,
        ),
        this.subscribeToTelemetryParams(
          subscriptionName,
          telemetryFields,
          destroy$,
          (data) => this.flespiTelemetryToVehicleTelemetry(data, deviceConfigMap),
          onDataCb,
        ),
        this.subscribeToCustomSensors(subscriptionName, destroy$, onDataCb),
      ],
      onAuthFailure,
    )
  }

  // Calculator statuses/odometer/engine-hours - fleet only, tsp devices have no calculators
  private subscribeToCalcMessages(
    subscriptionName: string,
    destroy$: Subject<void>,
    deviceConfigMap: Map<number, DeviceSensorsConfig>,
    statusIntervalsStateMap: Map<number, Map<StatusIntervalType, StatusIntervalState>>,
    onDataCb: (data: VehicleTelemetry[]) => void,
  ): SubscriptionItem {
    const calcMessages$ = new Subject<FlespiCalcMessage>()
    // not react on moving_status and idling calcs via subscription
    const notReactCalc = ['moving_status', 'idling_status']
    // Handle calculator messages in chunks every second
    calcMessages$
      .pipe(takeUntil(destroy$), buffer(calcMessages$.pipe(debounceTime(1000))))
      .subscribe((messages) => {
        onDataCb(
          messages
            .filter((item) => !notReactCalc.includes(item?.data?.name))
            // Sort ASC by the timestamp of the last update
            .sort((a, b) => a.data.end - b.data.end)
            .map((message) => {
              return this.flespiCalcsToVehicleTelemetry(
                message,
                deviceConfigMap,
                statusIntervalsStateMap,
              )
            })
            .filter((telemetry) => Boolean(telemetry?.deviceId)) as VehicleTelemetry[],
        )
      })

    return new SubscriptionItem({
      topicPattern: 'flespi/state/gw/calcs/{calc_id}/devices/{device_id}/last',
      shareMode: false,
      topicFilter: '',
      sessionName: subscriptionName,
      messageHandler: (message: FlespiCalcMessage) => calcMessages$.next(message),
    })
  }

  // Custom sensor parameters - fleet only, tsp devices don't have configurable custom sensors
  private subscribeToCustomSensors(
    subscriptionName: string,
    destroy$: Subject<void>,
    onDataCb: (data: VehicleTelemetry[]) => void,
  ): SubscriptionItem {
    const customSensorsMessages$ = new Subject<FlespiTopicMessage<boolean>>()
    customSensorsMessages$
      .pipe(takeUntil(destroy$), buffer(customSensorsMessages$.pipe(debounceTime(1000))))
      .subscribe((messages) => {
        const customSensorsMap = new Map<number, Record<string, any>>()
        messages.forEach((message) => {
          const propertyName = message.topicData.param
          const customSensorId = this.getCustomSensorIdByParameterName(propertyName)
          if (message.topicData.device_id && propertyName && customSensorId) {
            const deviceId = parseInt(message.topicData.device_id)
            let data = customSensorsMap.get(deviceId)
            if (!data) {
              data = { deviceId, customSensors: {} }
              customSensorsMap.set(deviceId, data)
            }
            data.customSensors[customSensorId] = message.data
          }
        })
        onDataCb(Array.from(customSensorsMap.values()) as VehicleTelemetry[])
      })

    return new SubscriptionItem({
      topicPattern: 'flespi/state/gw/devices/{device_id}/telemetry/{param}',
      shareMode: false,
      // message=topics[6]~"custom_sensor_*" - url_encoded
      topicFilter: `message=topics%5B6%5D~%22${CustomSensorPrefix}%2A%22`,
      sessionName: subscriptionName,
      messageHandler: (message: FlespiTopicMessage) => customSensorsMessages$.next(message),
    })
  }

  protected getCustomSensorIdByParameterName(parameterName: string) {
    if (parameterName.startsWith(CustomSensorPrefix)) {
      return parameterName.replace(CustomSensorPrefix, '')
    } else {
      return ''
    }
  }

  // TODO: Consider removing unused method
  protected getTelemetryFieldsToLoad(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    vehicles: Vehicle[],
  ) {
    // const odometerFields = uniq(
    //   vehicles
    //     .map((v) => {
    //       const config = v.primaryDevice?.sensorsConfig
    //       return config &&
    //         config.odometer_source_type === OdometerSourceTypes.telemetry &&
    //         config.odometer_parameter
    //         ? config.odometer_parameter
    //         : ''
    //     })
    //     .filter(Boolean),
    // )
    // return odometerFields.length
    //   ? [...this.TELEMETRY_FIELDS, ...odometerFields]
    //   : this.TELEMETRY_FIELDS
    return this.TELEMETRY_FIELDS
  }

  protected getDeviceConfigMap(vehicles: Vehicle[]): Map<number, DeviceSensorsConfig> {
    return new Map(
      vehicles
        .map((vehicle) => [
          vehicle.primaryExternalDeviceId,
          vehicle.primaryDevice?.sensorsConfig ?? {},
        ])
        .filter((data) => data[0] && data[1]) as Array<[number, DeviceSensorsConfig]>,
    )
  }

  protected flespiCalcsToVehicleTelemetry(
    message: FlespiTopicMessage,
    deviceConfigMap: Map<number, DeviceSensorsConfig>,
    statusIntervalsStateMap: Map<number, Map<StatusIntervalType, StatusIntervalState>>,
  ): VehicleTelemetry | undefined {
    const deviceId = parseInt(message.topicData.device_id)
    // Create an interval-state map instance for device
    let deviceMap = statusIntervalsStateMap.get(deviceId)
    if (!deviceMap) {
      deviceMap = new Map()
      statusIntervalsStateMap.set(deviceId, deviceMap)
    }

    // Save status interval updates to the map
    if (
      message.data.name &&
      (message.data.name === 'moving_status' || message.data.name === 'idling_status')
    ) {
      deviceMap.set(message.data.name, {
        active: message.data.active,
        startTime: Math.round(message.data.begin * 1000),
        endTime: Math.round(message.data.end * 1000),
      })
    }

    // Do not use calculator odometer value if not configured
    const deviceConfig = deviceConfigMap.get(deviceId)
    if (
      message.data.name &&
      message.data.name === 'odometer' &&
      deviceConfig?.odometer_source_type &&
      deviceConfig.odometer_source_type !== OdometerSourceTypes.calculator
    ) {
      return
    }

    // Do not use calculator engine hours value if not configured
    if (
      message.data.name &&
      message.data.name === 'engine_hours' &&
      deviceConfig?.engine_hours_source_type &&
      deviceConfig.engine_hours_source_type !== EngineHoursSourceTypes.calculator
    ) {
      return
    }

    switch (message.data.name) {
      case 'moving_status':
        if (message.data.active) {
          // Prevent Stopped status from overwriting Idling status
          if (!message.data.moving) {
            const idlingIntervalState = deviceMap.get('idling_status')
            if (idlingIntervalState && idlingIntervalState.active) {
              return undefined
            }
          }

          return {
            deviceId,
            lastStatus: message.data.moving ? VehicleStatus.Running : VehicleStatus.Stopped,
            lastStatusChangeAt: Math.round(message.data.begin * 1000),
            timestamps: { lastStatus: Math.round(message.data.end * 1000) },
          } as VehicleTelemetry
        }
        break
      case 'idling_status':
        if (message.data.active) {
          return {
            deviceId,
            lastStatus: VehicleStatus.Idling,
            lastStatusChangeAt: Math.round(message.data.begin * 1000),
            timestamps: stripEmptyKeys({
              lastStatus: Math.round(message.data.end * 1000),
              idlingLastGapTimestamp: message.data.last_gap_timestamp
                ? Math.round(message.data.last_gap_timestamp * 1000)
                : undefined,
            }),
          } as VehicleTelemetry
        }
        break
      case 'odometer':
        return {
          deviceId,
          odometer: message.data.total_mileage,
          timestamps: { odometer: Math.round(message.data.end * 1000) },
        } as VehicleTelemetry
      case 'engine_hours':
        return {
          deviceId,
          engineHours: Math.round(message.data.total_duration / 3600),
          timestamps: { engineHours: Math.round(message.data.end * 1000) },
        } as VehicleTelemetry
    }
  }

  protected flespiTelemetryToVehicleTelemetry(
    data: FlespiTelemetryData,
    deviceConfigMap: Map<number, DeviceSensorsConfig>,
    // Point in time the freshness checks (e.g. overspeeding) should be evaluated against.
    // Real-time callers use "now"; historical playback passes the message's own timestamp.
    referenceTime: number = Date.now(),
  ): VehicleTelemetry {
    const deviceId = data['device.id']?.value
    const deviceConfig = deviceConfigMap.get(deviceId)

    let dtcCodes = data['ti_dtc_codes']?.value
    if (dtcCodes) {
      if (!Array.isArray(dtcCodes)) {
        dtcCodes = [dtcCodes]
      }
      dtcCodes = dtcCodes.map((codeItem: any) => {
        return getDtcCodeString(codeItem)
      })
    }

    const customSensors: Record<string, any> = {}
    Object.keys(data).forEach((key) => {
      const customSensorId = this.getCustomSensorIdByParameterName(key)
      if (customSensorId && data[key]?.value !== undefined) {
        customSensors[customSensorId] = data[key].value
      }
    })

    const lastServerTimestamp =
      typeof data['server.timestamp']?.value === 'number'
        ? Math.round(data['server.timestamp']?.value * 1000)
        : undefined
    const lastTimestamp =
      typeof data['timestamp']?.value === 'number'
        ? Math.round(data['timestamp']?.value * 1000)
        : undefined
    // use server.timestamp only if it is and timestamp is from future
    const lastReportedAt =
      lastServerTimestamp && lastTimestamp && lastTimestamp > lastServerTimestamp
        ? lastServerTimestamp
        : lastTimestamp

    const positionValidOrNoValidData =
      data['position']?.value?.valid === true ||
      data['position']?.value?.valid === undefined ||
      data['position']?.value?.valid === null

    const telemetry = {
      deviceId: data['device.id']?.value,
      inSleepMode: data['sleep.mode.status']?.value,
      lastReportedAt,
      battery: data['ti_vehicle_battery']?.value,
      fuelLevel: data['ti_fuel_level']?.value,
      ignitionStatus:
        typeof data['ti_ignition_status']?.value === 'number'
          ? Boolean(data['ti_ignition_status']?.value)
          : typeof data['ti_ignition_status']?.value === 'boolean'
            ? data['ti_ignition_status']?.value
            : undefined,
      engineRPM: data['ti_engine_rpm']?.value,
      engineCoolantTemperature: data['ti_engine_coolant_temperature']?.value,
      engineOilTemperature: data['ti_engine_oil_temperature']?.value,
      dtcCodes,
      evBatteryLevel: data['ti_vehicle_ev_battery_level']?.value,
      seatBeltStatus: data['ti_seatbelt_status']?.value,
      vin: data['ti_vehicle_vin']?.value,
      altitude: positionValidOrNoValidData ? data['position']?.value?.altitude : undefined,
      direction: positionValidOrNoValidData ? data['position']?.value?.direction : undefined,
      latitude: positionValidOrNoValidData ? data['position']?.value?.latitude : undefined,
      longitude: positionValidOrNoValidData ? data['position']?.value?.longitude : undefined,
      speed: positionValidOrNoValidData ? data['position']?.value?.speed : undefined,
      postedSpeed: data['wialon.speed.limit']?.value,
      overspeeding:
        data['wialon.overspeeding.event'] &&
        data['wialon.overspeeding.event'].ts * 1000 >= referenceTime - 30 * ONE_SECOND_MS
          ? !!data['wialon.overspeeding.event']?.value
          : undefined,
      valid: data['position']?.value?.valid,
      address: positionValidOrNoValidData ? data['wialon.address']?.value : undefined,
      locationType: 'address',
      customSensors: Object.keys(customSensors).length ? customSensors : undefined,
      timestamps: stripEmptyKeys({
        position:
          positionValidOrNoValidData && data['position']?.ts
            ? Math.round(data['position'].ts * 1000)
            : undefined,
        ignitionStatus: data['ti_ignition_status']?.ts
          ? Math.round(data['ti_ignition_status']?.ts * 1000)
          : null,
      }),
    } as VehicleTelemetry

    if (deviceConfig && deviceConfig.odometer_source_type === OdometerSourceTypes.telemetry) {
      // The ti_vehicle_mileage parameter is calculated automatically based on odometer_parameter config
      telemetry.odometer = data['ti_vehicle_mileage']?.value
    }

    if (
      deviceConfig &&
      deviceConfig.engine_hours_source_type === EngineHoursSourceTypes.telemetry
    ) {
      // The ti_engine_hours parameter is calculated automatically based on engine_hours_parameter config
      telemetry.engineHours = data['ti_engine_hours']?.value
    }

    return stripEmptyKeys(telemetry)
  }

  protected loadTotalEngineHours(deviceId: string | number) {
    return this.api.fleet.vehicle.totalEngineHours(deviceId)
  }

  protected loadTotalMileage(deviceId: string | number) {
    return this.api.fleet.vehicle.totalMileage(deviceId)
  }

  protected loadMassCurrentStatuses(deviceIds: number[]) {
    return this.api.fleet.vehicle.massCalculatedCurrentStatus(deviceIds)
  }

  protected loadMassMessages(vehicleIds: string[], count: number) {
    return this.api.fleet.vehicle.massMessages(vehicleIds, {
      vehicleIds,
      fields: ['device.id', 'position.latitude', 'position.longitude', 'timestamp'],
      filter: POSITION_VALID_CHECK,
      count,
      reverse: true,
      timestampTo: Date.now() - 1,
    })
  }

  protected async loadFlespiToken() {
    return this.api.fleet.user.obtainFlespiToken()
  }
}

type FlespiCalcMessage = FlespiTopicMessage<{
  name: string
  active: boolean
  begin: number
  end: number
  [key: string]: any
}>

type StatusIntervalType = 'moving_status' | 'idling_status'
type StatusIntervalState = { active: boolean; startTime: number; endTime: number }

/**
 * Derives a vehicle status from speed/ignition for a single historical snapshot, mirroring
 * the fallback logic in TelemetryProvider.enrichTelemetryUpdate. Unlike real-time, this does
 * not use the moving/idling calculator intervals from `loadHistoricalStatuses`, so short
 * idling periods may be reported as Running - acceptable for now, revisit if needed.
 */
function deriveHistoricalVehicleStatus(telemetry: VehicleTelemetry): VehicleStatus {
  if (telemetry.speed) return VehicleStatus.Running
  if (telemetry.ignitionStatus) return VehicleStatus.Idling
  return VehicleStatus.Stopped
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    const serialized = JSON.stringify(value)
    if (typeof serialized !== 'string') {
      throw new HistoricalTelemetryUnavailableError('a boundary message cannot be compared')
    }
    return serialized
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(',')}]`
  }

  const record = value as Record<string, unknown>
  const fields = Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
  return `{${fields.join(',')}}`
}

/** Intervals without `total_mileage_at_start` (older data) keep their end total from begin. */
function toOdometerCalcValues(interval: any): HistoricalCalculatorValue[] {
  const beginMs = Math.round(interval.begin * 1000)
  const endMs = Math.round(interval.end * 1000)
  const total: number = interval.total_mileage
  const atStart = interval.total_mileage_at_start
  if (!Number.isFinite(atStart)) {
    return [{ value: total, fromMs: beginMs, measuredAtMs: endMs }]
  }

  // Summing step_mileage undercounts the interval mileage
  const mileageSteps: any[] = Array.isArray(interval.mileage_steps) ? interval.mileage_steps : []
  const steps = mileageSteps
    .filter(
      (step) =>
        Number.isFinite(step?.step_timestamp) &&
        Number.isFinite(step?.mileage_since_interval_start) &&
        step.mileage_since_interval_start >= 0 &&
        atStart + step.mileage_since_interval_start <= total + 1e-6,
    )
    .map((step) => {
      const timestampMs = Math.round(step.step_timestamp * 1000)
      return {
        value: atStart + step.mileage_since_interval_start,
        fromMs: timestampMs,
        measuredAtMs: timestampMs,
      }
    })

  return [
    { value: atStart, fromMs: beginMs, measuredAtMs: beginMs },
    ...steps,
    { value: total, fromMs: endMs, measuredAtMs: endMs },
  ]
}

/**
 * Binary-searches the last value with `fromMs <= timestampMs` in a list sorted ascending.
 * Entries reported before the first value still get it - the calculator hadn't produced a
 * result yet, but it's the earliest known one.
 */
function findCalcValueAtOrBefore(
  values: HistoricalCalculatorValue[],
  timestampMs: number,
): HistoricalCalculatorValue {
  let lo = 0
  let hi = values.length - 1
  let result = values[0]

  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (values[mid].fromMs <= timestampMs) {
      result = values[mid]
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  return result
}

export const mergeIntervals = <I extends { begin: number; end: number }>(inputData: I[][]): I[] => {
  // Flatten the input array and add priority as a property
  const intervalsWithPriority: Array<I & { priority: number }> = []

  inputData.forEach((intervals: any, priority: any) => {
    intervals.forEach((interval: any) => {
      intervalsWithPriority.push({ ...interval, priority })
    })
  })

  intervalsWithPriority.sort((a, b) => a.begin - b.begin || a.priority - b.priority)

  const result: Array<I & { priority: number }> = []
  for (const current of intervalsWithPriority) {
    if (!result.length || result[result.length - 1].end <= current.begin) {
      // No overlap, add the interval
      result.push({ ...current })
    } else {
      const last = result[result.length - 1]
      // Overlap exists, handle based on priority
      if (last.end > current.begin) {
        if (last.priority <= current.priority) {
          // Split last interval and add current interval if it has higher priority
          if (last.begin < current.begin) {
            result[result.length - 1] = { ...last, end: current.begin }
            result.push({ ...current })
          } else {
            result[result.length - 1] = { ...current }
          }
        } else {
          // Adjust the overlapping portion of last interval
          if (current.end > last.end) {
            result.push({ ...current, begin: last.end })
          }
        }
      }
    }
  }

  return result
}
